import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useState,
    useRef
} from "react";
import { FORMS, MODULES } from "../../../const";
import { supplierPaymentHoldValidation } from "../../validations/SupplierPaymentHoldCreation";
import LoadingSpinner from "../../LoadingSpinner";
import Dropdown from "../../input/Dropdown";
import Input from "../../input/Input";
import {
    getSupplierPaymentHoldSuppliers,
    getSupplierPaymentHoldJobs,
} from "../../../api/finance/supplierPaymentHoldApi";
import useUserCompany from "../../../hooks/useUserCompany";
import SearchDropdown from "../../input/SearchDropdown";

const FORM = FORMS.SUPPLIER_PAYMENT_HOLD.HEADER.form;

export const REF_TYPE_OPTIONS = [
    { key: 1, value: 1, text: "Supplier" },
    { key: 2, value: 2, text: "Job" },
];

const SupplierPaymentHoldComponent = forwardRef(
    ({ errors, dropdown, isLoading, currentValues, editMode }, ref) => {
        const userCompany = useUserCompany();
        const [company, setCompany] = useState(
            userCompany ? userCompany.comId : null
        );

        const searchDropdownRef = useRef();

        useEffect(() => {
            setCompany(userCompany ? userCompany.comId : null);
        }, [userCompany]);

        const [supplierPaymentHoldData, setSupplierPaymentHoldData] = useState(
            editMode
                ? { ...currentValues }
                : {
                    sphEvInvType: "",
                    sphRefType: "",
                    sphReferences: [],
                    sphReason: "",
                }
        );
        const [currentErrors, setCurrentErrors] = useState({});
        const [selectedItems, setSelectedItems] = useState([]);

        useEffect(() => {
            setCurrentErrors(errors);
        }, [errors]);

        useImperativeHandle(ref, () => ({
            getData: () => supplierPaymentHoldData,
            validateForm: () =>
                supplierPaymentHoldValidation(supplierPaymentHoldData),
            resetForm: () => {
                setSupplierPaymentHoldData({
                    sphEvInvType: "",
                    sphRefType: "",
                    sphReferences: [],
                    sphReason: "",
                });
                setSelectedItems([]);
                searchDropdownRef.current?.reset();
            },
        }));

        useEffect(() => {
            const options = dropdown.evInvTypeOptions || [];
            if (options.length === 1 && !supplierPaymentHoldData.sphEvInvType) {
                setSupplierPaymentHoldData((prevState) => ({
                    ...prevState,
                    sphEvInvType: options[0].value,
                }));
            }
        }, [dropdown.evInvTypeOptions]);

        const searchReferencesAPI = async (searchParam) => {
            const text = typeof searchParam === "string" ? searchParam : searchParam?.searchText || "";
            if (!text || text.length < 3) return [];

            const { sphEvInvType, sphRefType } = supplierPaymentHoldData;

            try {
                const options = sphRefType === 1
                    ? await getSupplierPaymentHoldSuppliers({ evInvType: sphEvInvType, company, searchText: text })
                    : await getSupplierPaymentHoldJobs({ evInvType: sphEvInvType, company, searchText: text });

                return options;
            } catch (error) {
                return [];
            }
        };

        const handleRemoveItem = (valToRemove) => {
            const newItems = selectedItems.filter(item => item.value !== valToRemove);
            setSelectedItems(newItems);
            setSupplierPaymentHoldData(prev => ({
                ...prev,
                sphReferences: newItems.map(i => i.value)
            }));
        };

        return (
            <>
                {isLoading ? (
                    <LoadingSpinner />
                ) : (
                    <form className="ui form" style={{ padding: "10px 16px" }}>
                        <div className="ui segment">
                            <h4 className="ui deviding header">Payment Hold Details</h4>

                            <div className="two fields">
                                <Dropdown
                                    required={true}
                                    label="EV/INV"
                                    error={currentErrors.sphEvInvType}
                                    module={MODULES.Finance}
                                    form={FORM}
                                    field="sph_ev_inv_type"
                                    editMode={editMode}
                                    resetError={() =>
                                        setCurrentErrors((prevState) => {
                                            const { sphEvInvType: _, ...rest } = prevState;
                                            return rest;
                                        })
                                    }
                                    placeholder="EV/INV"
                                    options={dropdown.evInvTypeOptions}
                                    value={supplierPaymentHoldData.sphEvInvType}
                                    style={{ height: "20px" }}
                                    onChange={(e, data) => {
                                        setSupplierPaymentHoldData((prevState) => ({
                                            ...prevState,
                                            sphEvInvType: data.value,
                                            sphReferences: [],
                                        }));
                                        setSelectedItems([]);
                                        searchDropdownRef.current?.reset();
                                        setCurrentErrors((prevState) => {
                                            const { sphReferences: _, ...rest } = prevState;
                                            return rest;
                                        });
                                    }}
                                />

                                <Dropdown
                                    required={true}
                                    label="Type"
                                    error={currentErrors.sphRefType}
                                    module={MODULES.Finance}
                                    form={FORM}
                                    field="sph_ref_type"
                                    editMode={editMode}
                                    resetError={() =>
                                        setCurrentErrors((prevState) => {
                                            const { sphRefType: _, ...rest } = prevState;
                                            return rest;
                                        })
                                    }
                                    placeholder="Type"
                                    options={REF_TYPE_OPTIONS}
                                    value={supplierPaymentHoldData.sphRefType}
                                    style={{ height: "20px" }}
                                    onChange={(e, data) => {
                                        setSupplierPaymentHoldData((prevState) => ({
                                            ...prevState,
                                            sphRefType: data.value,
                                            sphReferences: [],
                                        }));
                                        setSelectedItems([]);
                                        searchDropdownRef.current?.reset();
                                        setCurrentErrors((prevState) => {
                                            const { sphReferences: _, ...rest } = prevState;
                                            return rest;
                                        });
                                    }}
                                />
                            </div>

                            <div className={`field ${currentErrors.sphReferences ? "error" : ""}`}>
                                <SearchDropdown
                                    ref={searchDropdownRef}
                                    label={supplierPaymentHoldData.sphRefType === 2 ? "Job / Request Numbers" : "Suppliers"}
                                    required={true}
                                    api={searchReferencesAPI}
                                    returnFullObject={true}
                                    disabled={!supplierPaymentHoldData.sphEvInvType || !supplierPaymentHoldData.sphRefType}
                                    onResultSelect={(result) => {
                                        if (!result) return;

                                        if (!selectedItems.find(item => item.value === result.value)) {
                                            const newItems = [...selectedItems, result];
                                            setSelectedItems(newItems);

                                            setSupplierPaymentHoldData(prev => ({
                                                ...prev,
                                                sphReferences: newItems.map(i => i.value)
                                            }));
                                        }

                                        searchDropdownRef.current?.reset();

                                        setCurrentErrors((prevState) => {
                                            const { sphReferences: _, ...rest } = prevState;
                                            return rest;
                                        });
                                    }}
                                    error={currentErrors.sphReferences}
                                    resetError={() =>
                                        setCurrentErrors((prevState) => {
                                            const { sphReferences: _, ...rest } = prevState;
                                            return rest;
                                        })
                                    }
                                />

                                {selectedItems.length > 0 && (
                                    <div className="ui labels" style={{ marginTop: '10px' }}>
                                        {selectedItems.map(item => (
                                            <div className="ui large label" key={item.value} style={{ marginBottom: '5px' }}>
                                                {item.title}
                                                <i
                                                    className="delete icon"
                                                    onClick={() => handleRemoveItem(item.value)}
                                                    style={{ cursor: "pointer" }}
                                                ></i>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <Input
                                label="Reason"
                                required={true}
                                placeholder="Reason"
                                module={MODULES.Finance}
                                form={FORM}
                                field="sph_reason"
                                editMode={editMode}
                                error={currentErrors.sphReason}
                                resetError={() =>
                                    setCurrentErrors((prevState) => {
                                        const { sphReason: _, ...rest } = prevState;
                                        return rest;
                                    })
                                }
                                value={supplierPaymentHoldData.sphReason}
                                onChange={(e) => {
                                    setSupplierPaymentHoldData((prevState) => ({
                                        ...prevState,
                                        sphReason: e.target.value,
                                    }));
                                }}
                            />
                        </div>
                    </form>
                )}
            </>
        );
    }
);

export default SupplierPaymentHoldComponent;